import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFounderData, saveFounderData, FounderData } from '../services/founderService';

const AdminDashboardPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [founderData, setFounderData] = useState<FounderData>({
    name: 'Deborah Lawson',
    picture: '/images/loom.jpeg',
    title: 'Founder, CEO & Creative Director',
    bio: `Deborah Lawson is a visionary textile designer and entrepreneur whose passion for sustainable luxury has revolutionized the textile industry. Born in Barcelona to a family of traditional weavers, Deborah's journey began at the age of 12 when she first learned to operate her grandmother's antique loom.

After graduating with honors from the prestigious Central Saint Martins in London, Deborah spent a decade working with some of the world's most renowned fashion houses, including Chanel, Hermès, and Valentino. Her innovative approach to sustainable materials and traditional craftsmanship earned her recognition as one of the industry's most promising young designers.

In 2003, at the age of 28, Deborah founded Vogueloom with a bold vision: to create textiles that would not only meet the highest standards of luxury and quality but would also set new benchmarks for environmental responsibility. Her groundbreaking work with organic fibers, natural dyes, and zero-waste production methods has earned Vogueloom numerous industry awards and certifications.

Under Deborah's leadership, Vogueloom has grown from a small workshop to an internationally recognized brand, collaborating with luxury hotels, prestigious interior design firms, and sustainable fashion labels. Her commitment to preserving traditional weaving techniques while embracing cutting-edge technology has made Vogueloom a beacon of innovation in the textile world.

Deborah is also a passionate advocate for women in business and sustainable design. She serves on the board of several environmental organizations and regularly speaks at international conferences about the future of sustainable luxury textiles. Her TED Talk on "The Art of Sustainable Luxury" has been viewed by millions worldwide.

When not in her studio or traveling to source the finest materials, Deborah can be found mentoring young designers, teaching master classes in traditional weaving, or exploring ancient textile traditions in remote villages around the world. Her personal collection of antique looms and textiles is considered one of the most comprehensive in Europe.`
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // Load founder data from Firebase (with localStorage fallback)
    const loadData = async () => {
      try {
        const data = await getFounderData();
        if (data) {
          setFounderData(data);
        }
      } catch (error) {
        console.error('Error loading founder data:', error);
        // Fallback to localStorage if Firebase fails
        const savedData = localStorage.getItem('founder_data');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFounderData(parsed);
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Disable submit button to prevent double submissions
    const submitButton = e.currentTarget.querySelector('button[type="submit"]') as HTMLButtonElement;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Saving...';
    }
    
    try {
      // Save to Firebase (always saves to localStorage first)
      const result = await saveFounderData(founderData);
      setMessage({ 
        type: result.success ? 'success' : 'error', 
        text: result.message 
      });
    } catch (error) {
      console.error('Unexpected error saving:', error);
      setMessage({ 
        type: 'error', 
        text: 'An unexpected error occurred. Data has been saved locally.' 
      });
    } finally {
      // Re-enable submit button
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Save Changes';
      }
    }
    
    // Clear message after 5 seconds
    setTimeout(() => {
      setMessage(null);
    }, 5000);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please upload an image file.' });
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image size must be less than 5MB.' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFounderData({ ...founderData, picture: base64String });
      };
      reader.onerror = () => {
        setMessage({ type: 'error', text: 'Error reading image file.' });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFounderData({ ...founderData, picture: '/images/loom.jpeg' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-midnight via-deep-purple to-royal-purple py-12 px-4">
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 mb-8 border border-lavender/30">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-serif font-bold text-gradient mb-2">Admin Dashboard</h1>
              <p className="text-sage">Manage founder information for the About page</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-6 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-lavender/30">
          <h2 className="text-2xl font-serif font-bold text-gradient mb-6">Edit Founder Information</h2>

          {message && (
            <div
              className={`mb-6 px-4 py-3 rounded-xl ${
                message.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-charcoal mb-2">
                Founder Name
              </label>
              <input
                type="text"
                id="name"
                value={founderData.name}
                onChange={(e) => setFounderData({ ...founderData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-lavender/30 focus:outline-none focus:ring-2 focus:ring-warm-gold focus:border-transparent text-charcoal"
                placeholder="Enter founder name"
                required
              />
            </div>

            <div>
              <label htmlFor="title" className="block text-sm font-semibold text-charcoal mb-2">
                Title
              </label>
              <input
                type="text"
                id="title"
                value={founderData.title}
                onChange={(e) => setFounderData({ ...founderData, title: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-lavender/30 focus:outline-none focus:ring-2 focus:ring-warm-gold focus:border-transparent text-charcoal"
                placeholder="Enter title (e.g., Founder, CEO & Creative Director)"
                required
              />
            </div>

            <div>
              <label htmlFor="picture" className="block text-sm font-semibold text-charcoal mb-2">
                Founder Picture
              </label>
              <div className="space-y-3">
                <div className="flex items-center space-x-4">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="w-full px-4 py-3 rounded-xl border-2 border-dashed border-lavender/30 hover:border-warm-gold transition-colors text-center text-charcoal">
                      <span className="text-warm-gold font-semibold">📷 Upload Image</span>
                      <p className="text-sm text-sage mt-1">Click to select an image from your device</p>
                    </div>
                  </label>
                  {founderData.picture && founderData.picture !== '/images/loom.jpeg' && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-sage">OR</span>
                  <input
                    type="text"
                    value={founderData.picture.startsWith('data:') ? '' : founderData.picture}
                    onChange={(e) => setFounderData({ ...founderData, picture: e.target.value })}
                    className="flex-1 px-4 py-3 rounded-xl border border-lavender/30 focus:outline-none focus:ring-2 focus:ring-warm-gold focus:border-transparent text-charcoal"
                    placeholder="/images/loom.jpeg"
                  />
                  <span className="text-sm text-sage">Enter URL</span>
                </div>
              </div>
              <p className="mt-2 text-sm text-sage">
                Upload an image file (max 5MB) or enter an image URL path
              </p>
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-semibold text-charcoal mb-2">
                Founder Bio / Writeup
              </label>
              <textarea
                id="bio"
                value={founderData.bio}
                onChange={(e) => setFounderData({ ...founderData, bio: e.target.value })}
                rows={12}
                className="w-full px-4 py-3 rounded-xl border border-lavender/30 focus:outline-none focus:ring-2 focus:ring-warm-gold focus:border-transparent text-charcoal resize-y"
                placeholder="Enter the founder's biography..."
                required
              />
              <p className="mt-2 text-sm text-sage">
                Write the founder's biography. Each paragraph will be displayed separately on the About page.
              </p>
            </div>

            {/* Preview */}
            <div className="mt-6 p-6 bg-gradient-accent rounded-xl">
              <h3 className="text-lg font-semibold text-charcoal mb-4">Preview</h3>
              <div className="bg-white rounded-xl p-4 space-y-4">
                <div className="flex items-center space-x-4">
                  <img
                    src={founderData.picture}
                    alt={founderData.name}
                    className="w-24 h-24 object-cover rounded-xl"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/loom.jpeg';
                    }}
                  />
                  <div>
                    <h4 className="text-xl font-serif font-bold text-gradient">{founderData.name}</h4>
                    <p className="text-royal-purple font-semibold">{founderData.title}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-lavender/20">
                  <p className="text-sm text-sage font-semibold mb-2">Bio Preview:</p>
                  <div className="text-sm text-sage line-clamp-3">
                    {founderData.bio.substring(0, 200)}...
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-warm-gold to-sunset-orange text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all duration-300"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={() => navigate('/about')}
                className="px-6 py-3 bg-white border border-lavender/30 text-charcoal font-semibold rounded-xl hover:bg-lavender/10 transition-colors"
              >
                View About Page
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;

